import { PageMeta } from '@/components/seo/PageMeta'
import { LegalContactCard, LegalPage, type LegalSection } from '@/components/landing/misc/LegalPage'

const sections: LegalSection[] = [
  {
    id: 'agreement',
    title: 'Agreement to Terms',
    body: (
      <>
        <p>
          These Terms of Service ("Terms") constitute a legally binding agreement between you and
          Templatean Corporation ("Templatean," "we," "us," or "our") governing your access to and
          use of our services, including our website, applications, Office Add-ins, and related services
          (collectively, the "Services").
        </p>
        <p>
          By accessing or using our Services, you agree to be bound by these Terms. If you do not agree
          to these Terms, you may not access or use the Services.
        </p>
      </>
    ),
  },
  {
    id: 'eligibility',
    title: 'Eligibility',
    body: (
      <p>
        You must be at least 16 years old to use our Services. By using the Services, you represent and
        warrant that you meet this age requirement. If you are using the Services on behalf of an organization,
        you represent that you have the authority to bind that organization to these Terms.
      </p>
    ),
  },
  {
    id: 'account-registration',
    title: 'Account Registration',
    body: (
      <>
        <p>To access certain features of our Services, you must create an account. When registering, you agree to:</p>
        <ul>
          <li>Provide accurate, current, and complete information</li>
          <li>Maintain and update your information to keep it accurate</li>
          <li>Keep your password secure and confidential</li>
          <li>Accept responsibility for all activities under your account</li>
          <li>Notify us immediately of any unauthorized access</li>
        </ul>
        <p>We reserve the right to suspend or terminate accounts that violate these Terms.</p>
      </>
    ),
  },
  {
    id: 'subscription-payment',
    title: 'Subscription and Payment',
    body: (
      <>
        <h3>Subscription Plans</h3>
        <p>
          Our Services are offered on a subscription basis. Details of available plans, features, and pricing
          are available on our website. We reserve the right to modify pricing with 30 days' notice.
        </p>

        <h3>Billing</h3>
        <p>
          By subscribing, you authorize us to charge your payment method on a recurring basis. All fees are
          non-refundable except as required by law or as explicitly stated in these Terms.
        </p>

        <h3>Cancellation</h3>
        <p>
          You may cancel your subscription at any time through your account settings. Cancellation takes
          effect at the end of your current billing period, and you will retain access until then.
        </p>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    title: 'Acceptable Use Policy',
    body: (
      <>
        <p>You agree not to use the Services to:</p>
        <ul>
          <li>Violate any applicable laws or regulations</li>
          <li>Infringe on intellectual property rights of others</li>
          <li>Upload malicious code or attempt to breach security</li>
          <li>Interfere with or disrupt the Services</li>
          <li>Harvest or collect user information without consent</li>
          <li>Use the AI features to generate harmful or misleading content</li>
          <li>Resell or redistribute the Services without authorization</li>
          <li>Attempt to reverse engineer our software</li>
        </ul>
      </>
    ),
  },
  {
    id: 'intellectual-property',
    title: 'Intellectual Property',
    body: (
      <>
        <h3>Our Content</h3>
        <p>
          The Services, including all content, features, and functionality, are owned by Templatean and are
          protected by copyright, trademark, and other intellectual property laws. You may not copy, modify,
          or distribute our content without explicit permission.
        </p>

        <h3>Your Content</h3>
        <p>
          You retain ownership of content you upload or create using our Services. By using the Services,
          you grant us a limited license to store, process, and display your content as necessary to provide
          the Services.
        </p>

        <h3>AI-Generated Content</h3>
        <p>
          Content generated by our AI features is licensed to you for use in accordance with these Terms.
          You are responsible for reviewing AI-generated content before use and ensuring it meets your needs.
        </p>
      </>
    ),
  },
  {
    id: 'office-add-ins',
    title: 'Office Add-in Terms',
    body: (
      <>
        <p>
          Our Office Add-ins integrate with Microsoft Office applications. By using these add-ins, you also
          agree to comply with Microsoft's applicable terms of service. The add-ins are provided "as-is" and
          may be subject to updates or changes.
        </p>
        <ul>
          <li>Add-ins require an active Templatean subscription</li>
          <li>Some features require internet connectivity</li>
          <li>Document content may be processed by our servers for AI features</li>
          <li>Performance may vary based on document size and complexity</li>
        </ul>
      </>
    ),
  },
  {
    id: 'service-availability',
    title: 'Service Availability',
    body: (
      <>
        <p>
          We strive to maintain high availability of our Services. However, we do not guarantee uninterrupted
          access and may perform maintenance or updates that temporarily affect availability. We will provide
          reasonable notice of planned maintenance when possible.
        </p>
        <p>For enterprise customers, specific uptime commitments may be included in your service level agreement.</p>
      </>
    ),
  },
  {
    id: 'warranties',
    title: 'Disclaimer of Warranties',
    body: (
      <>
        <p>
          THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
          OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </p>
        <p>
          We do not warrant that the Services will be error-free, that defects will be corrected, or that
          our servers are free of viruses or other harmful components.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of Liability',
    body: (
      <>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, TEMPLATEAN SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
          SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA,
          OR GOODWILL, ARISING OUT OF YOUR USE OF THE SERVICES.
        </p>
        <p>
          Our total liability for any claims arising under these Terms shall not exceed the amount you paid
          us in the twelve months preceding the claim.
        </p>
      </>
    ),
  },
  {
    id: 'indemnification',
    title: 'Indemnification',
    body: (
      <p>
        You agree to indemnify and hold harmless Templatean and its officers, directors, employees, and
        agents from any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising
        from your use of the Services, your violation of these Terms, or your violation of any rights of
        a third party.
      </p>
    ),
  },
  {
    id: 'termination',
    title: 'Termination',
    body: (
      <>
        <p>
          We may suspend or terminate your access to the Services at any time for violation of these Terms
          or for any other reason at our discretion. Upon termination:
        </p>
        <ul>
          <li>Your right to use the Services will immediately cease</li>
          <li>You may request export of your data within 30 days</li>
          <li>We may delete your account and data after the retention period</li>
          <li>Provisions that should survive termination will remain in effect</li>
        </ul>
      </>
    ),
  },
  {
    id: 'governing-law',
    title: 'Governing Law and Disputes',
    body: (
      <>
        <p>
          These Terms shall be governed by the laws of the State of New York, without regard to conflict
          of law principles. Any disputes arising under these Terms shall be resolved in the state or federal
          courts located in New York County, New York.
        </p>
        <p>
          For enterprise customers, alternative dispute resolution mechanisms may be specified in your
          service agreement.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to These Terms',
    body: (
      <p>
        We may update these Terms from time to time. We will notify you of material changes by email or
        through the Services at least 30 days before they take effect. Your continued use of the Services
        after the effective date constitutes acceptance of the updated Terms.
      </p>
    ),
  },
  {
    id: 'severability',
    title: 'Severability',
    body: (
      <p>
        If any provision of these Terms is found to be unenforceable, the remaining provisions will continue
        in full force and effect. The unenforceable provision will be modified to the minimum extent necessary
        to make it enforceable while preserving its intent.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact Us',
    body: (
      <>
        <p>If you have questions about these Terms of Service, please contact us:</p>
        <LegalContactCard email="legal@templatean.com" />
      </>
    ),
  },
]

export default function Terms() {
  return (
    <>
      <PageMeta
        title="Terms of Service | Templatean"
        description="Read the terms and conditions governing your use of Templatean services, including our Office Add-ins, AI features, and digital asset management platform."
      />
      <LegalPage
        title="Terms of"
        highlight="service."
        tone="purple"
        lede="The terms and conditions governing your use of Templatean services, including our Office Add-ins, AI features, and digital asset management platform."
        lastUpdated="January 28, 2026"
        sections={sections}
        related={{ label: 'Privacy Policy', href: '/privacy' }}
      />
    </>
  )
}
